<template>
  <main class="document-share-page">
    <div class="document-share-shell">
      <header class="document-toolbar">
        <div class="toolbar-title" :title="sharedDocument?.title || '共享文档'">
          {{ sharedDocument?.title || '共享文档' }}
        </div>
        <div v-if="state === 'ready'" class="toolbar-actions">
          <button
            v-if="wechatAvailable"
            class="toolbar-button"
            type="button"
            :disabled="sharing || wechatShareUnavailable"
            :title="wechatShareError || '配置分享卡片并查看分享引导'"
            @click="shareWechat"
          >{{ wechatShareButtonText }}</button>
          <button class="toolbar-button" type="button" @click="loadDocument">刷新</button>
          <a
            v-if="sharedDocument?.allowDownload && downloadUrl"
            class="toolbar-button primary-button"
            :href="downloadUrl"
            target="_blank"
            rel="noopener noreferrer"
            download
          >下载</a>
        </div>
      </header>

      <section v-if="state === 'loading'" class="document-state-panel" aria-live="polite">
        <span class="state-spinner" aria-hidden="true"></span>
        <h1>正在打开文档</h1>
        <p>请稍候，正在验证分享链接。</p>
      </section>

      <section v-else-if="state === 'password'" class="document-state-panel password-panel">
        <div class="state-mark" aria-hidden="true">🔒</div>
        <h1>请输入访问密码</h1>
        <p>该文档需要验证密码后才能查看。</p>
        <form class="password-form" @submit.prevent="submitPassword">
          <input
            v-model="password"
            type="password"
            autocomplete="current-password"
            placeholder="访问密码"
            aria-label="访问密码"
            autofocus
          />
          <button class="document-primary-button" type="submit" :disabled="loading">
            {{ loading ? '验证中' : '查看文档' }}
          </button>
        </form>
        <small v-if="errorMessage" class="form-error">{{ errorMessage }}</small>
      </section>

      <section v-else-if="state === 'error'" class="document-state-panel">
        <div class="state-mark error-mark" aria-hidden="true">!</div>
        <h1>{{ errorTitle }}</h1>
        <p>{{ errorMessage || '该分享链接当前无法访问。' }}</p>
        <button class="document-secondary-button" type="button" @click="loadDocument">重新尝试</button>
      </section>

      <article v-else class="document-body">
        <DocumentViewer
          :document="sharedDocument"
          :preview-url="previewUrl"
          :download-url="downloadUrl"
          :title="sharedDocument.title"
        />
      </article>
    </div>

    <div
      v-if="shareGuideVisible"
      class="share-guide-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-guide-title"
      @click.self="closeShareGuide"
    >
      <div class="share-guide-panel">
        <button
          class="share-guide-close"
          type="button"
          aria-label="关闭分享引导"
          @click="closeShareGuide"
        >×</button>
        <div class="share-guide-arrow" aria-hidden="true">↗</div>
        <p class="share-guide-kicker">分享卡片已准备好</p>
        <h2 id="share-guide-title">请点击右上角「···」</h2>
        <p class="share-guide-description">在微信菜单中选择「发送给朋友」，即可分享这篇文档。</p>
        <button class="share-guide-button" type="button" @click="closeShareGuide">知道了</button>
      </div>
    </div>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { showToast } from 'vant'
import DocumentViewer from '@/components/DocumentViewer.vue'
import { getSharedDocument, resolveDocumentResourceUrl } from '@/api/documents'
import {
  getWechatShareErrorMessage,
  getWechatShareUrl,
  isWechatBrowser,
  isWechatShareConfigurationUnavailable,
  isWechatShareEnabled,
  updateWechatAppMessageShareData
} from '@/utils/wechat-share'

const route = useRoute()
const state = ref('loading')
const loading = ref(false)
const sharing = ref(false)
const shareGuideVisible = ref(false)
const password = ref('')
const errorMessage = ref('')
const sharedDocument = ref(null)
const wechatShareError = ref('')
const wechatShareUnavailable = ref(false)
const originalTitle = document.title
const token = computed(() => String(route.params.token || ''))
const wechatAvailable = isWechatBrowser() && isWechatShareEnabled()
const wechatShareTitle = 'Vitaeai 智能体'
const wechatShareImage = '/images/vitaeai_logo.jpg'
const wechatShareButtonText = computed(() => {
  if (sharing.value) return '准备中'
  if (wechatShareUnavailable.value) return '微信分享未启用'
  return '分享给好友'
})

const previewUrl = computed(() => {
  if (!sharedDocument.value) return ''
  return resolveDocumentResourceUrl(
    sharedDocument.value.previewUrl,
    `/share-documents/${encodeURIComponent(token.value)}/file`,
    password.value ? { password: password.value } : {}
  )
})

const downloadUrl = computed(() => {
  if (!sharedDocument.value) return ''
  return resolveDocumentResourceUrl(
    sharedDocument.value.downloadUrl,
    `/share-documents/${encodeURIComponent(token.value)}/file`,
    password.value ? { password: password.value, download: 'true' } : { download: 'true' }
  )
})

const errorTitle = computed(() => {
  const message = errorMessage.value
  if (/密码|password/i.test(message)) return '访问密码不正确'
  if (/过期|expired/i.test(message)) return '分享链接已过期'
  if (/次数|访问上限|exhausted|limit/i.test(message)) return '访问次数已用尽'
  if (/撤销|失效|不存在|not found|invalid/i.test(message)) return '分享链接已失效'
  return '暂时无法打开文档'
})

async function loadDocument() {
  if (!token.value) {
    state.value = 'error'
    errorMessage.value = '分享链接缺少有效凭证。'
    return
  }

  state.value = 'loading'
  errorMessage.value = ''
  try {
    const result = await getSharedDocument(token.value, password.value)
    sharedDocument.value = result?.data || result
    if (!sharedDocument.value) throw new Error('文档内容为空')
    state.value = 'ready'
    document.title = sharedDocument.value.title ? `${sharedDocument.value.title} · VitaeAI` : '共享文档 · VitaeAI'
    updateDocumentShareMeta(sharedDocument.value)
    prepareWechatShare(sharedDocument.value)
  } catch (error) {
    const message = error?.message || '该分享链接当前无法访问。'
    errorMessage.value = message
    if (/密码|password/i.test(message)) {
      state.value = 'password'
      password.value = ''
    } else {
      state.value = 'error'
    }
  }
}

async function prepareWechatShare(documentValue) {
  if (!wechatAvailable) return
  wechatShareError.value = ''
  wechatShareUnavailable.value = false
  try {
    await updateWechatAppMessageShareData({
      title: wechatShareTitle,
      desc: documentValue.title || '共享文档',
      link: getWechatShareUrl(),
      imgUrl: new URL(wechatShareImage, window.location.origin).href
    })
  } catch (error) {
    wechatShareError.value = getWechatShareErrorMessage(error)
    wechatShareUnavailable.value = isWechatShareConfigurationUnavailable(error)
    console.warn('[wechat-share] 分享卡片配置失败', error)
  }
}

async function shareWechat() {
  if (!sharedDocument.value) return
  sharing.value = true
  shareGuideVisible.value = false
  wechatShareError.value = ''
  try {
    await updateWechatAppMessageShareData({
      title: wechatShareTitle,
      desc: sharedDocument.value.title || '共享文档',
      link: getWechatShareUrl(),
      imgUrl: new URL(wechatShareImage, window.location.origin).href
    })
    shareGuideVisible.value = true
  } catch (error) {
    wechatShareError.value = getWechatShareErrorMessage(error)
    wechatShareUnavailable.value = isWechatShareConfigurationUnavailable(error)
    showToast(wechatShareError.value)
    console.warn('[wechat-share] 分享卡片配置失败', error)
  } finally {
    sharing.value = false
  }
}

function closeShareGuide() {
  shareGuideVisible.value = false
}

function updateDocumentShareMeta(documentValue) {
  const title = documentValue.title || '共享文档'
  const description = documentValue.description || 'VitaeAI 共享文档'
  const image = new URL(wechatShareImage, window.location.origin).href
  const values = [
    ['og:title', title],
    ['og:description', description],
    ['og:url', getWechatShareUrl()],
    ['og:image', image]
  ]
  values.forEach(([property, content]) => {
    const meta = document.head.querySelector(`meta[property="${property}"][data-document-share="true"]`) || document.createElement('meta')
    meta.setAttribute('property', property)
    meta.setAttribute('content', content)
    meta.dataset.documentShare = 'true'
    if (!meta.parentNode) document.head.appendChild(meta)
  })
}

async function submitPassword() {
  if (!password.value.trim()) {
    errorMessage.value = '请输入访问密码。'
    return
  }
  loading.value = true
  errorMessage.value = ''
  await loadDocument()
  loading.value = false
}

onMounted(() => {
  const robots = document.createElement('meta')
  robots.name = 'robots'
  robots.content = 'noindex,nofollow,noarchive'
  robots.dataset.documentShare = 'true'
  document.head.appendChild(robots)
  loadDocument()
})

onBeforeUnmount(() => {
  shareGuideVisible.value = false
  document.title = originalTitle
  document.head.querySelectorAll('meta[data-document-share="true"]').forEach(meta => meta.remove())
})
</script>

<style scoped>
.document-share-page { height: 100vh; overflow: hidden; color: #1f2937; background: #fff; }
.document-share-shell { display: flex; height: 100%; min-height: 0; flex-direction: column; }
.document-toolbar { display: flex; min-height: 56px; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 16px; padding: 0 18px; border-bottom: 1px solid #dbe2ea; background: #f8fafc; }
.toolbar-title { min-width: 0; overflow: hidden; color: #111827; font-size: 15px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.toolbar-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 8px; }
.toolbar-button { display: inline-flex; min-height: 34px; align-items: center; justify-content: center; padding: 0 13px; border: 1px solid #cbd5e1; border-radius: 6px; color: #334155; background: #fff; font-size: 13px; font-weight: 600; text-decoration: none; cursor: pointer; }
.toolbar-button:hover { border-color: #94a3b8; background: #f1f5f9; }
.primary-button { border-color: #0f766e; color: #fff; background: #0f766e; }
.primary-button:hover { border-color: #115e59; background: #115e59; }
.document-body { display: flex; min-height: 0; flex: 1; flex-direction: column; overflow: hidden; background: #fff; }
.document-state-panel { display: grid; min-height: 0; flex: 1; place-items: center; align-content: center; padding: 28px; background: #fff; text-align: center; }
.document-state-panel h1 { margin: 14px 0 0; color: #111827; font-size: clamp(22px, 4vw, 34px); line-height: 1.2; }
.document-state-panel p { max-width: 680px; margin: 12px 0 0; color: #64748b; font-size: 15px; line-height: 1.7; }
.document-primary-button, .document-secondary-button { display: inline-flex; min-height: 42px; align-items: center; justify-content: center; border-radius: 6px; padding: 0 16px; font-size: 13px; font-weight: 700; text-decoration: none; cursor: pointer; }
.document-primary-button { border: 1px solid #0f766e; color: #fff; background: #0f766e; }
.document-secondary-button { border: 1px solid #cbd5e1; color: #334155; background: #fff; }
.document-primary-button:hover, .document-secondary-button:hover { border-color: #115e59; background: #115e59; color: #fff; }
.document-primary-button:disabled { cursor: wait; opacity: .6; }
.state-mark { display: grid; width: 56px; height: 56px; place-items: center; border: 1px solid #cbd5e1; border-radius: 50%; color: #334155; background: #f1f5f9; font-size: 22px; }
.error-mark { border-color: #fecaca; color: #b91c1c; background: #fef2f2; }
.state-spinner { width: 30px; height: 30px; border: 2px solid #ccfbf1; border-top-color: #0f766e; border-radius: 50%; animation: document-spin .8s linear infinite; }
.password-panel { display: flex; flex-direction: column; }
.password-form { display: flex; width: min(100%, 420px); gap: 10px; margin-top: 24px; }
.password-form input { min-width: 0; flex: 1; height: 42px; border: 1px solid #cbd5e1; border-radius: 6px; outline: none; padding: 0 12px; color: #1f2937; background: #fff; }
.password-form input:focus { border-color: #0f766e; box-shadow: 0 0 0 3px rgba(15,118,110,.12); }
.form-error { margin-top: 10px; color: #dc2626; }
.share-guide-overlay { position: fixed; z-index: 100; inset: 0; display: flex; align-items: flex-start; justify-content: center; padding: 86px 18px 24px; background: rgba(15, 23, 42, .58); }
.share-guide-panel { position: relative; width: min(100%, 380px); margin-top: 8px; border: 1px solid #dbe2ea; border-radius: 14px; padding: 28px 24px 24px; color: #1f2937; background: #fff; box-shadow: 0 18px 50px rgba(15, 23, 42, .22); text-align: center; }
.share-guide-close { position: absolute; top: 8px; right: 12px; width: 32px; height: 32px; border: 0; color: #64748b; background: transparent; font-size: 25px; line-height: 1; cursor: pointer; }
.share-guide-close:hover { color: #111827; }
.share-guide-arrow { position: absolute; top: -58px; right: 18px; color: #fff; font-size: 54px; font-weight: 700; line-height: 1; transform: rotate(-12deg); }
.share-guide-kicker { margin: 0; color: #0f766e; font-size: 13px; font-weight: 700; letter-spacing: .04em; }
.share-guide-panel h2 { margin: 10px 0 0; color: #111827; font-size: 22px; line-height: 1.3; }
.share-guide-description { margin: 12px 0 20px; color: #64748b; font-size: 14px; line-height: 1.7; }
.share-guide-button { min-height: 38px; border: 1px solid #0f766e; border-radius: 6px; padding: 0 18px; color: #fff; background: #0f766e; font-size: 13px; font-weight: 700; cursor: pointer; }
.share-guide-button:hover { border-color: #115e59; background: #115e59; }
@keyframes document-spin { to { transform: rotate(360deg); } }
@media (max-width: 640px) { .document-toolbar { min-height: 52px; padding: 0 12px; } .toolbar-button { min-height: 32px; padding: 0 10px; font-size: 12px; } .password-form { flex-direction: column; width: 100%; } .share-guide-overlay { padding: 74px 12px 20px; } .share-guide-panel { margin-top: 10px; padding: 26px 20px 20px; } .share-guide-arrow { top: -52px; right: 14px; font-size: 48px; } }
</style>
